import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Building2, Plus, Pencil, Trash2, MapPin, Phone, Mail, Clock, 
  ChevronRight, Layers, LayoutGrid, List, Search, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SucursalesModule({ academiaId, onSelectSucursal, categorias = [], viewMode = 'cards', onToggleViewMode, onSucursalesChange, onRefresh, refreshing = false }) {
  const [loading, setLoading] = useState(true);
  const [sucursales, setSucursales] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editando, setEditando] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState({
    nombre: '', direccion: '', telefono: '', email_contacto: '', horario: ''
  });

  // Obtener categorías de una sucursal
  const getCategoriasDeSucursal = (sucursalId) => {
    return categorias.filter(c => c.sucursal_id === sucursalId);
  };

  useEffect(() => {
    if (academiaId) loadSucursales();
  }, [academiaId]);

  const loadSucursales = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/academias/${academiaId}/sucursales`, { withCredentials: true });
      setSucursales(res.data);
      // Notificar al padre sobre el cambio de sucursales
      if (onSucursalesChange) {
        onSucursalesChange(res.data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (sucursal = null) => {
    if (sucursal) {
      setEditando(sucursal);
      setForm({
        nombre: sucursal.nombre || '',
        direccion: sucursal.direccion || '',
        telefono: sucursal.telefono || '',
        email_contacto: sucursal.email_contacto || '',
        horario: sucursal.horario || ''
      });
    } else {
      setEditando(null);
      setForm({ nombre: '', direccion: '', telefono: '', email_contacto: '', horario: '' });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      if (editando) {
        await axios.put(`${API}/academias/${academiaId}/sucursales/${editando.id}`, form, { withCredentials: true });
        toast.success('Sucursal actualizada');
      } else {
        await axios.post(`${API}/academias/${academiaId}/sucursales`, form, { withCredentials: true });
        toast.success('Sucursal creada');
      }
      setShowDialog(false);
      loadSucursales();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (sucursal) => {
    if (!window.confirm(`¿Eliminar la sucursal "${sucursal.nombre}"? Solo es posible si no tiene categorías asociadas.`)) return;
    
    try {
      await axios.delete(`${API}/academias/${academiaId}/sucursales/${sucursal.id}`, { withCredentials: true });
      toast.success('Sucursal eliminada');
      loadSucursales();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Sucursales</h1>
          <p className="text-slate-400 text-sm">Gestiona las ubicaciones de tu academia</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Toggle de vista */}
          <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1">
            <button
              onClick={() => onToggleViewMode?.('cards')}
              className={`p-1.5 rounded ${viewMode === 'cards' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
              title="Vista de tarjetas"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => onToggleViewMode?.('list')}
              className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
              title="Vista de lista"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => onRefresh?.()}
              disabled={refreshing}
              className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-600 disabled:opacity-50"
              title="Actualizar"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <Button onClick={() => handleOpenDialog()} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" /> Nueva Sucursal
          </Button>
        </div>
      </div>

      {/* Filtro de búsqueda */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Buscar sucursal..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>

      {(() => {
        const sucursalesFiltradas = sucursales.filter(s => 
          !searchTerm || s.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          s.direccion?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        return sucursalesFiltradas.length === 0 && sucursales.length === 0 ? (
        <Card className="p-8 bg-slate-800 border-slate-700 text-center">
          <Building2 className="w-16 h-16 mx-auto text-slate-500 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Sin Sucursales</h3>
          <p className="text-slate-400 mb-4">Crea sucursales para organizar tus categorías por ubicación</p>
          <Button onClick={() => handleOpenDialog()} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" /> Crear Primera Sucursal
          </Button>
        </Card>
      ) : sucursalesFiltradas.length === 0 ? (
        <Card className="p-8 bg-slate-800 border-slate-700 text-center">
          <Search className="w-12 h-12 mx-auto text-slate-500 mb-4" />
          <p className="text-slate-400">No se encontraron sucursales</p>
        </Card>
      ) : viewMode === 'cards' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sucursalesFiltradas.map(sucursal => (
            <Card 
              key={sucursal.id} 
              className="p-4 bg-slate-800 border-slate-700 hover:border-emerald-600/50 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold shrink-0">
                  <Building2 className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white truncate flex items-center gap-2">
                    {sucursal.nombre}
                    {!sucursal.activa && <Badge className="bg-slate-600 text-xs">Inactiva</Badge>}
                  </h3>
                  {sucursal.direccion && <p className="text-sm text-slate-400 truncate">{sucursal.direccion}</p>}
                  {sucursal.telefono && <p className="text-xs text-slate-500">{sucursal.telefono}</p>}
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <Badge className="bg-slate-700">
                  <Layers className="w-3 h-3 mr-1" />
                  {getCategoriasDeSucursal(sucursal.id).length} categorías
                </Badge>
              </div>

              {/* Categorías de la sucursal */}
              {getCategoriasDeSucursal(sucursal.id).length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-700">
                  <div className="flex flex-wrap gap-1">
                    {getCategoriasDeSucursal(sucursal.id).slice(0, 3).map(cat => (
                      <Badge key={cat.id} variant="outline" className="text-xs border-slate-600 text-slate-300">
                        {cat.nombre}
                      </Badge>
                    ))}
                    {getCategoriasDeSucursal(sucursal.id).length > 3 && (
                      <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                        +{getCategoriasDeSucursal(sucursal.id).length - 3}
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-slate-700 flex gap-2">
                <Button size="sm" variant="ghost" className="flex-1 text-slate-400 hover:text-white hover:bg-slate-600" onClick={() => handleOpenDialog(sucursal)}>
                  <Pencil className="w-3 h-3 mr-1" /> Editar
                </Button>
                <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-400 hover:bg-red-600/10" onClick={() => handleDelete(sucursal)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        /* Vista de lista */
        <Card className="bg-slate-800 border-slate-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Sucursal</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-300 hidden md:table-cell">Dirección</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-300 hidden lg:table-cell">Contacto</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Categorías</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {sucursalesFiltradas.map(sucursal => (
                <tr key={sucursal.id} className="hover:bg-slate-700/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white">
                        <Building2 className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{sucursal.nombre}</p>
                        {!sucursal.activa && <Badge className="bg-slate-600 text-xs">Inactiva</Badge>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-300 hidden md:table-cell">{sucursal.direccion || '-'}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {sucursal.telefono ? (
                      <div className="text-sm">
                        <p className="text-slate-300">{sucursal.telefono}</p>
                        {sucursal.email_contacto && <p className="text-slate-500 text-xs">{sucursal.email_contacto}</p>}
                      </div>
                    ) : <span className="text-slate-500">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className="bg-slate-700">{getCategoriasDeSucursal(sucursal.id).length}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white" onClick={() => handleOpenDialog(sucursal)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-400" onClick={() => handleDelete(sucursal)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      );
      })()}

      {/* Dialog Crear/Editar Sucursal */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Building2 className="w-5 h-5 text-emerald-400" />
              {editando ? 'Editar Sucursal' : 'Nueva Sucursal'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Nombre *</Label>
              <Input
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej: Sede Central, Sucursal Norte"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>

            <div>
              <Label className="text-slate-300">Dirección</Label>
              <Input
                value={form.direccion}
                onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                placeholder="Av. Principal 123, Ciudad"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Teléfono</Label>
                <Input
                  value={form.telefono}
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  placeholder="+56 9 1234 5678"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input
                  type="email"
                  value={form.email_contacto}
                  onChange={(e) => setForm({ ...form, email_contacto: e.target.value })}
                  placeholder="sucursal@email.com"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Horario de atención</Label>
              <Textarea
                value={form.horario}
                onChange={(e) => setForm({ ...form, horario: e.target.value })}
                placeholder="Lun-Vie: 9:00-20:00&#10;Sáb: 9:00-14:00"
                className="bg-slate-700 border-slate-600 text-white"
                rows={2}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1 border-slate-600">
                Cancelar
              </Button>
              <Button onClick={handleSave} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {editando ? 'Guardar Cambios' : 'Crear Sucursal'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
